<script setup lang="ts">
// The node's own settings (EditDrawer): name, site, tags, the delay buffer,
// NDI discovery and multicast, the PTP AES67 outputs and frame-aligned
// inputs follow, and
// the DeckLink IP cards' PTP, NMOS registry and ports (SMPTE 2110).
import { computed, reactive } from 'vue'
import type { EditTarget, ST2110Port } from '../../api'
import { list, useEditContext } from './context'

const props = defineProps<{ target: EditTarget }>()
const { node, cfg, player, ipv4 } = useEditContext(props.target)
const nodeForm = reactive({
    name: cfg.value.node.name,
    site: cfg.value.node.site ?? '',
    tags: (cfg.value.node.tags ?? []).join(', '),
    max_buffer_seconds: cfg.value.max_buffer_seconds,
    full_buffers: !!cfg.value.full_buffers,
    ndi_discovery: (cfg.value.ndi?.discovery_servers ?? []).join(', '),
    ndi_ips: (cfg.value.ndi?.extra_ips ?? []).join(', '),
    ndi_mc_recv: !cfg.value.ndi?.no_multicast_recv,
    ndi_mc_send: !!cfg.value.ndi?.multicast_send,
    ndi_mc_prefix: cfg.value.ndi?.multicast_prefix ?? '',
    ndi_mc_ttl: cfg.value.ndi?.multicast_ttl || '',
    ptp_domain: cfg.value.ptp?.domain ?? 0,
    ptp_interface: cfg.value.ptp?.interface ?? '',
})
// SMPTE 2110 (DECISIONS 2026-10-10 "SMPTE 2110"): shown when the machine
// lists a DeckLink IP card; each port starts as it is set, or as the card
// says it is
const ipDevices = computed(() => node.status?.machine.decklink_ip ?? [])
const st2110 = reactive({
    ptp_domain: cfg.value.st2110?.ptp_domain ?? '',
    ptp_follower_only: cfg.value.st2110?.ptp_follower_only ?? true, // a plant has its grandmaster
    nmos_registry: cfg.value.st2110?.nmos_registry ?? '',
    ports: ipDevices.value.flatMap((d) =>
        d.ethernet.map((e): ST2110Port => {
            const set = cfg.value.st2110?.ports?.find((p) => p.device === d.device && p.index === e.index)
            return (
                set ?? {
                    device: d.device,
                    index: e.index,
                    dhcp: e.dhcp,
                    address: e.address,
                    mask: e.mask,
                    gateway: e.gateway,
                }
            )
        }),
    ),
})
async function save() {
    await node.saveNode({
        name: nodeForm.name,
        site: nodeForm.site || undefined,
        tags: nodeForm.tags
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        max_buffer_seconds: Number(nodeForm.max_buffer_seconds),
        full_buffers: nodeForm.full_buffers,
        ndi: {
            discovery_servers: list(nodeForm.ndi_discovery),
            extra_ips: list(nodeForm.ndi_ips),
            no_multicast_recv: !nodeForm.ndi_mc_recv || undefined,
            multicast_send: nodeForm.ndi_mc_send || undefined,
            multicast_prefix: (nodeForm.ndi_mc_send && nodeForm.ndi_mc_prefix.trim()) || undefined,
            multicast_ttl: (nodeForm.ndi_mc_send && Number(nodeForm.ndi_mc_ttl)) || undefined,
        },
        // only when set: an untouched node keeps no ptp section
        ptp:
            Number(nodeForm.ptp_domain) || nodeForm.ptp_interface || cfg.value.ptp
                ? { domain: Number(nodeForm.ptp_domain) || undefined, interface: nodeForm.ptp_interface || undefined }
                : undefined,
        // only with a card: a node without one keeps no st2110 section
        st2110:
            ipDevices.value.length || cfg.value.st2110
                ? {
                      ptp_domain: st2110.ptp_domain === '' ? undefined : Number(st2110.ptp_domain),
                      ptp_follower_only: st2110.ptp_follower_only || undefined,
                      nmos_registry: st2110.nmos_registry.trim() || undefined,
                      ports: st2110.ports.map((p) =>
                          p.dhcp
                              ? { device: p.device, index: p.index, dhcp: true }
                              : {
                                    device: p.device,
                                    index: p.index,
                                    address: p.address?.trim(),
                                    mask: p.mask?.trim(),
                                    gateway: p.gateway?.trim() || undefined,
                                },
                      ),
                  }
                : undefined,
    })
}
defineExpose({ save, snapshot: () => JSON.stringify([nodeForm, st2110]) })
</script>

<template>
    <div class="mb-2.5"><label>Name</label><input v-model="nodeForm.name" required /></div>
    <div class="mb-2.5"><label>Site</label><input v-model="nodeForm.site" placeholder="Copenhagen" /></div>
    <div class="mb-2.5">
        <label>Tags (comma separated)</label><input v-model="nodeForm.tags" placeholder="rlcs, venue" />
    </div>
    <div v-if="!player" class="mb-2.5">
        <label>Maximum destination delay (seconds; each encode holds only what its delays need)</label
        ><input v-model="nodeForm.max_buffer_seconds" type="number" min="10" max="7200" />
    </div>
    <div v-if="!player" class="mb-2.5">
        <label class="flex items-center gap-2"
            ><input v-model="nodeForm.full_buffers" type="checkbox" class="w-auto" data-test="full-buffers" /> Buffer
            the maximum delay for every encode: any delay change jumps at once, without a reconnect (about 600 MB per 6
            Mbit/s encode and 2.4 GB per passthrough at 600 s)</label
        >
    </div>
    <details>
        <summary>NDI</summary>
        <div class="mb-2.5">
            <label>Discovery servers (host or host:port, comma separated)</label
            ><input v-model="nodeForm.ndi_discovery" placeholder="10.0.0.20" class="font-mono tabular-nums" />
        </div>
        <div class="mb-2.5">
            <label>Also look for sources at (IP addresses, comma separated)</label
            ><input v-model="nodeForm.ndi_ips" placeholder="10.1.2.30, 10.1.2.31" class="font-mono tabular-nums" />
        </div>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="nodeForm.ndi_mc_recv" type="checkbox" class="w-auto" /> receive by multicast when a
                sender offers it</label
            >
        </div>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="nodeForm.ndi_mc_send" type="checkbox" class="w-auto" /> this node's NDI outputs send by
                multicast</label
            >
        </div>
        <div v-if="nodeForm.ndi_mc_send" class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Multicast groups under</label
                ><input v-model="nodeForm.ndi_mc_prefix" placeholder="239.255.0.0" class="font-mono tabular-nums" />
            </div>
            <div class="mb-2.5">
                <label>TTL (hops)</label
                ><input v-model="nodeForm.ndi_mc_ttl" type="number" min="1" max="255" placeholder="1" />
            </div>
        </div>
        <p class="text-xs text-fg-3">
            Sources on this network are found by themselves (mDNS). A discovery server finds them across networks; the
            addresses above are asked directly, for machines neither reaches. An NDI input can also be given its
            sender's address. Saving restarts the NDI inputs and outputs.
        </p>
        <p class="text-xs text-fg-3">
            NDI® is a registered trademark of Vizrt NDI AB ·
            <a href="https://ndi.video/" target="_blank" rel="noopener">ndi.video</a>
        </p>
    </details>
    <details v-if="!player">
        <summary>PTP (AES67, frame-aligned inputs)</summary>
        <div class="mb-2.5 grid grid-cols-2 gap-2">
            <div>
                <label>PTP domain</label
                ><input v-model="nodeForm.ptp_domain" type="number" min="0" max="127" placeholder="0" />
            </div>
            <div>
                <label>Interface for frame-aligned inputs</label
                ><select v-model="nodeForm.ptp_interface" data-test="ptp-interface">
                    <option value="">none</option>
                    <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                    <option
                        v-if="nodeForm.ptp_interface && !ipv4.includes(nodeForm.ptp_interface)"
                        :value="nodeForm.ptp_interface"
                    >
                        {{ nodeForm.ptp_interface }} (not on this node)
                    </option>
                </select>
            </div>
        </div>
        <p class="text-xs text-fg-3">
            AES67 outputs are timed by the audio network's PTP grandmaster (a Dante device in AES67 mode, or a dedicated
            grandmaster); a frame-aligned input whose source has no timecode numbers its frames from the plant's. The
            node follows it and never leads; without one, both wait. It listens on the AES67 outputs' interface, or on
            the one above when there are none (they must be the same). Domain 0 is AES67's and Dante's default.
        </p>
    </details>
    <details v-if="!player && ipDevices.length" data-test="st2110-node">
        <summary>SMPTE 2110 (DeckLink IP)</summary>
        <div class="mb-2.5 grid grid-cols-2 gap-2">
            <div>
                <label>PTP domain</label
                ><input v-model="st2110.ptp_domain" type="number" min="0" max="127" placeholder="the card's (127)" />
            </div>
            <div>
                <label>NMOS registry</label
                ><input v-model="st2110.nmos_registry" class="font-mono" placeholder="found on the network" />
            </div>
        </div>
        <label class="mb-2.5 flex items-center gap-1.5"
            ><input v-model="st2110.ptp_follower_only" type="checkbox" class="w-auto" /> follow the plant's PTP, never
            lead</label
        >
        <div v-for="p in st2110.ports" :key="p.device + p.index" class="mb-2 text-xs">
            <div class="mb-1 text-fg-2">{{ p.device }}, port {{ p.index + 1 }}</div>
            <label class="mb-1 flex items-center gap-1.5"
                ><input v-model="p.dhcp" type="checkbox" class="w-auto" /> address by DHCP</label
            >
            <div v-if="!p.dhcp" class="grid grid-cols-3 gap-1.5">
                <input v-model="p.address" class="font-mono" placeholder="10.1.0.21" aria-label="address" />
                <input v-model="p.mask" class="font-mono" placeholder="255.255.255.0" aria-label="mask" />
                <input v-model="p.gateway" class="font-mono" placeholder="gateway (none)" aria-label="gateway" />
            </div>
        </div>
        <p class="text-xs text-fg-3">
            The card's own settings, written into it within 5 s of saving (and when a card is found). Each input or
            output on its devices sets that device's flows.
        </p>
    </details>
</template>
